import React, { useState, useEffect } from 'react';
import FilterDropdown from '../FilterDropdown';
import { Badge } from "@/components/ui/badge";
import { X, Calendar, ArrowUpDown, RefreshCw, CheckSquare, XSquare, List, Tags, AlertTriangle } from "lucide-react";
import { ChangeType, EVENT_CHANGES } from '@/utils/eventLabels';
import { PricingEvent, ApprovalFilterType, setApprovalFilter } from '@/store/eventsSlice';
import { Button } from "@/components/ui/button";
import { checkAdminAuthorization } from "@/utils/authHelpers";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';

interface EventsFiltersProps {
  events: PricingEvent[];
  selectedEventTypes: ChangeType[];
  selectedQuarters: string[];
  selectedCategories: string[];
  onEventTypeChange: (value: string | string[]) => void;
  onQuarterChange: (value: string | string[]) => void;
  onCategoryChange: (value: string | string[]) => void;
  onRemoveFilter: (type: 'eventType' | 'quarter' | 'category', valueToRemove?: string) => void;
  onRefresh?: () => void;
}

export function EventsFilters({
  events,
  selectedEventTypes,
  selectedQuarters,
  selectedCategories,
  onEventTypeChange,
  onQuarterChange,
  onCategoryChange,
  onRemoveFilter,
  onRefresh
}: EventsFiltersProps) {
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const approvalFilter = useAppSelector(state => state.events.approvalFilter);
  const pages = useAppSelector(state => state.supaPages.pages);

  useEffect(() => {
    const checkAuth = async () => {
      const adminStatus = await checkAdminAuthorization(navigate, (status) => {
        setIsAdmin(status);
      });
      setIsAdmin(adminStatus);
    };
    checkAuth();
  }, [navigate]);

  // Get unique quarters from events
  const uniqueQuarters = ['All', ...Array.from(new Set(events.map(event => {
    const year = event.v1.substring(0, 4);
    const month = parseInt(event.v1.substring(4, 6));
    return `Q${Math.ceil(month / 3)} ${year}`;
  })))];

  // Get unique event types
  const uniqueEventTypes = ['All' as ChangeType, ...Object.keys(EVENT_CHANGES) as ChangeType[]];

  // Get unique categories from pages
  const uniqueCategories = ['All', ...Array.from(new Set(pages.map(page => page.category)))];

  // Options for approval filter
  const approvalOptions: ApprovalFilterType[] = ['All', 'Approved', 'Not Approved', 'Error'];

  const renderApprovalIcon = (option: ApprovalFilterType) => {
    switch (option) {
      case 'Approved':
        return <CheckSquare className="h-4 w-4" />;
      case 'Not Approved':
        return <XSquare className="h-4 w-4" />;
      case 'Error':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <List className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-2 flex-1">
          <FilterDropdown
            label="Event Type"
            options={uniqueEventTypes}
            value={selectedEventTypes}
            onChange={onEventTypeChange}
            isMulti={true}
            displayAllText="All Event Types"
            formatOptionLabel={(option: string) => EVENT_CHANGES[option as ChangeType]?.label || option}
          />
          <FilterDropdown
            label="Quarter"
            options={uniqueQuarters}
            value={selectedQuarters}
            onChange={onQuarterChange}
            isMulti={true}
            displayAllText="All Quarters"
          />
          <FilterDropdown
            label="Category"
            options={uniqueCategories}
            value={selectedCategories}
            onChange={onCategoryChange}
            isMulti={true}
            displayAllText="All Categories"
          />
          {isAdmin && (
            <>
              <FilterDropdown
                label="Approval Status"
                options={approvalOptions}
                value={approvalFilter}
                onChange={(value) => dispatch(setApprovalFilter(value as ApprovalFilterType))}
                formatOptionLabel={(option: string) => (
                  <div className="flex items-center gap-2">
                    {renderApprovalIcon(option as ApprovalFilterType)}
                    <span>{option}</span>
                  </div>
                )}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={onRefresh}
                className="h-10 w-10"
                title="Refresh Events"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {selectedEventTypes.map(eventType =>
          eventType !== 'All' && (
            <Badge
              key={`event-${eventType}`}
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1 bg-transparent hover:bg-gray-50 text-gray-700 border border-gray-300"
            >
              <ArrowUpDown className="w-3 h-3" />
              {EVENT_CHANGES[eventType]?.label || eventType}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => onRemoveFilter('eventType', eventType)}
              />
            </Badge>
          )
        )}
        {selectedQuarters.map(quarter =>
          quarter !== 'All' && (
            <Badge
              key={`quarter-${quarter}`}
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1 bg-transparent hover:bg-gray-50 text-gray-700 border border-gray-300"
            >
              <Calendar className="w-3 h-3" />
              {quarter}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => onRemoveFilter('quarter', quarter)}
              />
            </Badge>
          )
        )}
        {selectedCategories.map(category =>
          category !== 'All' && (
            <Badge
              key={`category-${category}`}
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1 bg-transparent hover:bg-gray-50 text-gray-700 border border-gray-300"
            >
              <Tags className="w-3 h-3" />
              {category}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => onRemoveFilter('category', category)}
              />
            </Badge>
          )
        )}
      </div>
    </div>
  );
}